<template>
  <v-app id="app" :style="mainStyle"
         class="protected-app">

    <fin-top-navigation @toggle="()=> $refs.sideNav.toggle()"/>
    <fin-payment-status-check/>

    <fin-side-navigation ref="sideNav"
                         :hasContextHelp="hasContextHelp"
                         :showPopup="showPopup"
                         @closePopup="()=> showPopup=false"/>

    <v-main style="overflow: hidden"
            class="protected-app-main">
      <idle-check/>
      <fin-regulations-check/>

      <router-view/>
      <fin-cookie-declaration/>
      <fin-help-center-button
        v-if="$vuetify.breakpoint.smAndUp && hasContextHelp && (profile && profile.documentsToAccept.length === 0)"
        :showPopup="showPopup && (!isMobile || (isMobile && $vuetify.breakpoint.mdAndUp))"
        @closePopup="()=> showPopup=false"/>
      <fin-tour
        v-if="autoRunTour && show"
        auto-run
        :tourID="`autorun_${this.contextID || this.$route.name}`"
        :tourName="'autorun_'+autoRunTour.primary.tour_name[0].text"
        :steps="autoRunTour.fields"
      />
      <fin-mobile-search v-if="$vuetify.breakpoint.xs" v-model="showMobileSearchDialog"/>
    </v-main>

    <fin-footer v-if="isNotApp && $vuetify.breakpoint.smAndUp"
                :routes="routes"/>

    <fin-alert/>
    <fin-confirm/>
    <fin-subscription-popup/>

    <fin-lexicon-overlay/>
    <fin-help-center/>
    <fin-demo-tool-overlay/>
  </v-app>
</template>

<script>
import finSideNavigation from '@/components/navigation/sideNavigation/SideNavigationWrapper'
import FinAlert from '@/components/common/alert/index'
import FinConfirm from '@/components/common/confirm/index'
import FinLexiconOverlay from '@/components/common/lexicon/LexiconOverlay'
import FinDemoToolOverlay from '@/components/common/demoTool/DemoToolOverlay'
import FinTopNavigation from '@/components/navigation/TopNavigation'
import FinFooter from '@/components/common/footer/index'
import FinCookieDeclaration from "@/components/common/FinCookieDeclaration"
import FinHelpCenter from "@/components/common/HelpCenter"
import FinHelpCenterButton from "@/components/common/HelpCenter/HelpCenterButton"
import {EventBus} from "@/plugins/fincraft"
import {ReferenceTool} from '@/components/common/lexicon/LexiconRefferenceTool'
import LexiconAPI from "@/components/common/lexicon/LexiconAPI"
import IdleCheck from "@/views/protected/idleCheck"
import finBrowser from "@/js/finBrowser"
import FinTour from "@/components/common/Tour"
import {mapState} from "vuex"
import FinMobileSearch from "@/components/common/MobileSearch"
import FinSubscriptionPopup from "@/components/common/subscription/popup"
import FinPaymentStatusCheck from "@/views/protected/paymentStatusCheck"
import FinRegulationsCheck from "@/views/protected/regulationsCheck"

function parseSlice(slice, parserMapping, uid) {
  //TODO same like in lexion
  if (['content', 'example'].includes(slice.type)) {
    slice.primary.text.forEach(text => {
      ReferenceTool.parseWords(text, parserMapping, uid)
    })
  } else if (['tour'].includes(slice.type)) {
    slice.fields.forEach(({content}) => {
      ReferenceTool.parseWords(content, parserMapping, uid)
    })
  } else {
    console.log('ignore slice ' + slice.type)
  }
}

function basisConvert(doc, parserIndex) {
  //convert rest result so graphql
  doc.data._meta = {
    tags: doc.tags,
    id: doc.id,
    uid: doc.uid
  }

  doc.data.body = doc.data.body.map(part => {
    return {
      ...part,
      type: part.slice_type,
      fields: part.items
    }
  })

  doc.data.body.forEach(slice => {
    parseSlice(slice, parserIndex, doc.data._meta.uid)
  })
}

export default {
  name: 'ProtectedApp',
  components: {
    FinRegulationsCheck,
    FinPaymentStatusCheck,
    FinSubscriptionPopup,
    FinTour,
    FinMobileSearch,
    IdleCheck,
    FinHelpCenterButton,
    FinHelpCenter,
    FinCookieDeclaration,
    FinFooter,
    FinTopNavigation,
    FinLexiconOverlay,
    FinDemoToolOverlay,
    FinConfirm,
    FinAlert,
    finSideNavigation
  },
  data: () => ({
    routes: [
      {to: {name: 'imprintProtected'}, name: 'Impressum'},
      {to: {name: 'dataProtectionProtected'}, name: 'Datenschutz'},
      {to: {name: 'finAgb'}, name: 'AGB'},
      {to: {name: 'riskWarningsProtected'}, name: 'Risikohinweise'}
    ],
    isNotApp: !finBrowser.IS_IOS_OR_ANDROID_APP,
    isMobile: finBrowser.IS_MOBILE(),
    showMobileSearchDialog: false,
    previewDoc: null,
    autoRunTour: null,
    hasContextHelp: false,
    show: false,
    showPopup: false,
  }),
  props: {
    contextID: {
      type: String
    }
  },
  computed: {
    ...mapState({
      profile: state => state.auth.profile
    }),
    localStorageId() {
      return `helpCenter_popup_${this.profile.id}`
    },
    mainStyle() {
      let style = {}

      // the onboarding (which is mapped to finProfiler / finStrategist) has a white background instead of the other protected pages
      if (this.$route.fullPath.includes('finProfiler') || this.$route.fullPath.includes('finStrategist')) {
        style.backgroundColor = '#fff !important'
      }

      return style
    },
  },
  methods: {
    async init() {
      const route = this.contextID || this.$route.name
      this.show = await LexiconAPI.hasContextHelp(route)
      this.autoRunTour = await LexiconAPI.loadAutoRunTour(route)
      let storageItem = localStorage.getItem(this.localStorageId)
      if (storageItem !== undefined) {
        this.showPopup = !(storageItem === 'true')
      } else {
        this.showPopup = true
      }
    },
    async preview() {
      const doc = JSON.parse(JSON.stringify(this.previewDoc))
      if (doc.type === 'lexiconentry') {
        await this.previewLexicon(doc)
      } else if (doc.type === 'contexthelpentry') {
        await this.previewContextHelp(doc)
      }

    },
    async previewContextHelp(doc) {
      const parserIndex = LexiconAPI.getParserIndex()
      basisConvert(doc, parserIndex)

      console.log('modified', doc)
      EventBus.$emit('injectHelpCenter', doc.data)
    },
    async previewLexicon(doc) {
      //TODO use IdHelper.adaptUid()

      const parserIndex = LexiconAPI.getParserIndex()

      basisConvert(doc, parserIndex)

      const {opposite} = doc.data
      if (opposite) {
        delete doc.data.opposite
        if (opposite.id) {
          const oppositeDoc = await this.$prismic.client.getByID(opposite.id)
          oppositeDoc.data._meta = {
            tags: oppositeDoc.tags,
            id: oppositeDoc.id,
            uid: oppositeDoc.uid
          }
          doc.data.opposite = oppositeDoc.data
          delete oppositeDoc.data.body
          delete oppositeDoc.data.opposite
        }
      }

      EventBus.$emit("injectLexiconContent", doc.data)
    },
    showSearch(val) {
      this.showMobileSearchDialog = val
    }
  },
  watch: {
    $route: {
      async handler() {
        try {
          await this.init()
          const route = this.$route.name
          this.hasContextHelp = await LexiconAPI.hasContextHelp(route)
        } catch (e) {
          this.hasContextHelp = false
        }

      },
      immediate: true
    }
  },
  async mounted() {
    const docId = this.$route.query.preview
    if (docId) {
      const doc = await this.$prismic.client.getByID(docId)
      this.previewDoc = doc
      const prismicMenue = document.querySelector('#prismic-toolbar-v2')
      if (prismicMenue) {
        const wrapper = document.createElement('div')
        wrapper.style.backgroundColor = '#5163ba'
        wrapper.style.color = 'white'
        wrapper.style.cursor = 'pointer'
        wrapper.style.padding = '15px'
        wrapper.style.marginLeft = '5px'
        wrapper.style.borderRadius = '6px'
        wrapper.innerHTML = 'open'
        wrapper.onclick = this.preview
        prismicMenue.shadowRoot.querySelector('.Toolbar').append(wrapper)
      }
    }

    EventBus.$on('showMobileSearch', this.showSearch)

  },
  beforeDestroy() {
    EventBus.$off('showMobileSearch', this.showSearch)
  }
}
</script>

<style lang="scss" scoped>
</style>
